import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';

import { ClientConfigPanel, slugify } from '../components/ClientConfigPanel.js';
import { ConfigCard } from '../components/ConfigCard.js';
import {
  ApiError,
  isVlessClient,
  me as meApi,
  type ConfigEntry,
  type SerializedClientConfig,
  type SerializedConfig,
  type ServerOption,
  type VpnProtocol,
} from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/** Mirrors `MAX_BATCH_DEVICES` in `packages/api/src/routes/configs.ts`. */
const MAX_BATCH_DEVICES = 10;

type CreateState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; reason: string }
  /**
   * Multi-device issuance landed. We keep the freshly-minted client payloads
   * here (NOT in sessionStorage — see ConfigDetail's single-device flow for
   * the rationale on why one-shot reveal is preferred over persistent
   * cabinet storage). When `entries.length === 1` the dashboard navigates to
   * the existing detail page after acknowledgement; for N>1 we render an
   * inline reveal block with one collapsible spoiler per device.
   */
  | { kind: 'revealing'; entries: ConfigEntry[] };

export function DashboardPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [configs, setConfigs] = useState<SerializedConfig[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [createState, setCreateState] = useState<CreateState>({ kind: 'idle' });
  const [deviceNames, setDeviceNames] = useState<string[]>(['']);
  const [servers, setServers] = useState<ServerOption[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>('');

  const refreshConfigs = useCallback(async () => {
    try {
      const data = await meApi.listConfigs();
      setConfigs(data.items);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        await auth.logout();
        navigate('/login', { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : 'Не удалось загрузить конфиги.');
    }
  }, [auth, navigate]);

  useEffect(() => {
    void refreshConfigs();
  }, [refreshConfigs]);

  // Load the available servers once so the create form can offer a picker.
  // Failure is non-fatal — the form falls back to the API's default server.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await meApi.listServers();
        if (cancelled) return;
        setServers(data.servers);
        setSelectedServer(data.defaultServerId);
      } catch {
        // Ignore — issuing without a serverId uses the default server.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = deviceNames.map((n) => n.trim()).filter((n) => n.length > 0);
    if (trimmed.length === 0) {
      setError('Введите имя хотя бы одного устройства.');
      return;
    }
    if (trimmed.length > MAX_BATCH_DEVICES) {
      setError(`За один раз можно создать не больше ${MAX_BATCH_DEVICES} конфигов.`);
      return;
    }
    const seen = new Set<string>();
    for (const name of trimmed) {
      const k = name.toLocaleLowerCase('ru');
      if (seen.has(k)) {
        setError('Имена устройств должны быть уникальны.');
        return;
      }
      seen.add(k);
    }
    setError(null);
    setCreateState({ kind: 'submitting' });
    try {
      const resp = await meApi.createConfigs(trimmed, selectedServer || undefined);
      if (resp.configs.length === 1) {
        // Preserve the single-device UX: stash and navigate to the detail
        // page so the existing ConfigDetail flow handles the one-shot reveal.
        const entry = resp.configs[0]!;
        sessionStorage.setItem(
          `vpn.client.${entry.config.id}`,
          JSON.stringify(entry.client),
        );
        setCreateState({ kind: 'idle' });
        setDeviceNames(['']);
        await refreshConfigs();
        navigate(`/configs/${entry.config.id}`);
        return;
      }
      setCreateState({ kind: 'revealing', entries: resp.configs });
      setDeviceNames(['']);
      await refreshConfigs();
    } catch (err) {
      setCreateState({
        kind: 'error',
        reason: err instanceof Error ? err.message : 'Не удалось выпустить конфиги.',
      });
    }
  };

  const onReissue = async (id: string) => {
    if (!confirm('Перевыпустить конфиг? Старый ключ перестанет работать.')) return;
    setBusyId(id);
    try {
      const resp = await meApi.reissueConfig(id);
      // Stash the freshly-minted client config so the detail page can render
      // it once. We use sessionStorage so it survives the navigation but
      // doesn't outlive the tab.
      sessionStorage.setItem(`vpn.client.${resp.config.id}`, JSON.stringify(resp.client));
      await refreshConfigs();
      navigate(`/configs/${resp.config.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось перевыпустить конфиг.');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Отозвать конфиг? Это действие необратимо.')) return;
    setBusyId(id);
    try {
      await meApi.deleteConfig(id);
      await refreshConfigs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отозвать конфиг.');
    } finally {
      setBusyId(null);
    }
  };

  const onLogout = async () => {
    await auth.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <Header userName={auth.me?.user.name ?? ''} onLogout={onLogout} />

      {error && (
        <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {createState.kind === 'revealing' ? (
        <BatchRevealBlock
          entries={createState.entries}
          onAcknowledge={() => setCreateState({ kind: 'idle' })}
        />
      ) : (
        <CreateForm
          deviceNames={deviceNames}
          setDeviceNames={setDeviceNames}
          servers={servers}
          selectedServer={selectedServer}
          setSelectedServer={setSelectedServer}
          state={createState}
          onSubmit={onCreate}
          onReset={() => setCreateState({ kind: 'idle' })}
        />
      )}

      <section className="mt-10">
        <h2 className="text-lg font-semibold text-slate-200">Мои конфиги</h2>
        {configs === null && <div className="mt-3 text-sm text-slate-500">Загружаем…</div>}
        {configs !== null && configs.length === 0 && (
          <div className="mt-3 rounded-lg border border-dashed border-slate-800 px-4 py-8 text-center text-sm text-slate-500">
            Пока нет ни одного конфига. Создайте первый — выше.
          </div>
        )}
        {configs && configs.length > 0 && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {configs.map((c) => (
              <ConfigCard
                key={c.id}
                config={c}
                onReissue={() => onReissue(c.id)}
                onDelete={() => onDelete(c.id)}
                busy={busyId === c.id}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Header({ userName, onLogout }: { userName: string; onLogout: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onLogout();
    } finally {
      setBusy(false);
    }
  };
  return (
    <header className="mb-8 flex items-center justify-between border-b border-slate-800 pb-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-100">VPN-кабинет</h1>
        <p className="mt-0.5 text-sm text-slate-500">{userName}</p>
      </div>
      <div className="flex items-center gap-3">
        <Link to="/app" className="text-sm text-slate-400 hover:text-white">
          Приложение
        </Link>
        <button
          onClick={handleClick}
          disabled={busy}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? 'Выходим…' : 'Выйти'}
        </button>
      </div>
    </header>
  );
}

function CreateForm({
  deviceNames,
  setDeviceNames,
  servers,
  selectedServer,
  setSelectedServer,
  state,
  onSubmit,
  onReset,
}: {
  deviceNames: string[];
  setDeviceNames: (v: string[]) => void;
  servers: ServerOption[];
  selectedServer: string;
  setSelectedServer: (v: string) => void;
  state: CreateState;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onReset: () => void;
}) {
  if (state.kind === 'error') {
    return (
      <section className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
        <h3 className="text-sm font-semibold text-red-300">Не удалось выпустить конфиги</h3>
        <p className="mt-1 text-sm text-slate-300">{state.reason}</p>
        <button
          onClick={onReset}
          className="mt-4 rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark"
        >
          Попробовать снова
        </button>
      </section>
    );
  }

  const submitting = state.kind === 'submitting';
  const filledCount = deviceNames.filter((n) => n.trim().length > 0).length;
  const canAddMore = deviceNames.length < MAX_BATCH_DEVICES;

  const updateName = (idx: number, value: string) => {
    const next = deviceNames.slice();
    next[idx] = value;
    setDeviceNames(next);
  };
  const removeAt = (idx: number) => {
    if (deviceNames.length === 1) {
      setDeviceNames(['']);
      return;
    }
    const next = deviceNames.slice();
    next.splice(idx, 1);
    setDeviceNames(next);
  };
  const addRow = () => {
    if (!canAddMore) return;
    setDeviceNames([...deviceNames, '']);
  };

  const selectedOption = servers.find((s) => s.id === selectedServer);

  const submitLabel = submitting
    ? 'Выпускаем…'
    : filledCount > 1
      ? `Выпустить ${filledCount} конфигов`
      : 'Выпустить';

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
      <h2 className="text-sm font-semibold text-slate-200">Новые конфиги</h2>
      <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-3">
        {servers.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-xs text-slate-400">
              Сервер
              {/*
                A native <option> renders as plain text — it cannot hold a
                styled element — so the chip sits on the label and follows the
                selection, while the options carry the protocol as a text
                prefix. Either way the server's `label` stays free of it.

                Rendered off the resolved server rather than off `protocol`
                alone: an id matching nothing means we don't know the
                protocol, and an empty slot beats a confident "AWG".
              */}
              {selectedOption && <ProtocolChip protocol={selectedOption.protocol} />}
            </span>
            <select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-brand"
            >
              {servers.map((s) => (
                <option key={s.id} value={s.id}>
                  {`${protocolName(s.protocol)} · ${s.label}`}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex flex-col gap-2">
          {deviceNames.map((name, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <label className="flex-1">
                {idx === 0 && (
                  <span className="text-xs text-slate-400">Имя устройства</span>
                )}
                <input
                  value={name}
                  onChange={(e) => updateName(idx, e.target.value)}
                  placeholder="iPhone, MacBook…"
                  maxLength={64}
                  className={`${idx === 0 ? 'mt-1 ' : ''}w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-brand`}
                />
              </label>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label="Убрать устройство"
                disabled={deviceNames.length === 1 && name.length === 0}
                className={`${idx === 0 ? 'mt-5 ' : ''}rounded-lg border border-slate-700 px-2 py-2 text-xs text-slate-400 hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-30`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={addRow}
            disabled={!canAddMore}
            className="self-start rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Добавить устройство
            {!canAddMore && ` (макс. ${MAX_BATCH_DEVICES})`}
          </button>
          <button
            type="submit"
            disabled={submitting || filledCount === 0}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-dark disabled:opacity-60"
          >
            {submitLabel}
          </button>
        </div>
      </form>
      <p className="mt-3 text-xs text-slate-500">
        Конфиги выпускаются сразу. Администратор получит уведомление в Telegram и сможет
        отозвать выпуск, если он не был легитимным.
      </p>
    </section>
  );
}

/**
 * Display name of a protocol. Uppercased in code rather than baked into each
 * server's `label`, so `VPN_SERVERS` entries can be named after the place
 * («Амстердам») and the protocol still shows up in the UI.
 *
 * Older API bundles don't send `protocol`; those servers are AmneziaWG.
 */
function protocolName(protocol: VpnProtocol | undefined): string {
  return (protocol ?? 'awg').toUpperCase();
}

/** Small protocol badge — the styled counterpart of {@link protocolName}. */
function ProtocolChip({ protocol }: { protocol: VpnProtocol | undefined }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide ${
        protocol === 'vless' ? 'bg-brand/20 text-brand' : 'bg-slate-800 text-slate-300'
      }`}
    >
      {protocolName(protocol)}
    </span>
  );
}

/**
 * Multi-device reveal block. Each peer is wrapped in a `<details>` spoiler so
 * the user can expand them one by one; the bulk-download buttons at the top
 * pack every config into a single `.zip` (one per format) so they don't have
 * to scrape each spoiler manually.
 *
 * Private keys live in component state only — no sessionStorage round-trip,
 * so a refresh wipes them. The acknowledgement button just resets the
 * dashboard to its idle state.
 */
function BatchRevealBlock({
  entries,
  onAcknowledge,
}: {
  entries: ConfigEntry[];
  onAcknowledge: () => void;
}) {
  const [busyArchive, setBusyArchive] = useState<'awg' | 'amnezia' | 'vless' | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const downloadArchive = async (kind: 'awg' | 'amnezia' | 'vless') => {
    setBusyArchive(kind);
    setArchiveError(null);
    try {
      const zip = new JSZip();
      const usedNames = new Set<string>();
      for (const entry of entries) {
        const ext = kind === 'vless' ? 'txt' : kind === 'awg' ? 'conf' : 'vpn';
        const body = archiveBodyFor(entry.client, kind);
        const baseName = slugify(entry.config.name);
        // Disambiguate collisions deterministically — slugify can collapse
        // distinct human names into the same file name (e.g. "iPhone 12" and
        // "iPhone 13" both → "iphone").
        let name = `${baseName}.${ext}`;
        let i = 2;
        while (usedNames.has(name)) {
          name = `${baseName}-${i}.${ext}`;
          i++;
        }
        usedNames.add(name);
        zip.file(name, body);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        kind === 'vless'
          ? 'vless-configs.zip'
          : kind === 'awg'
            ? 'amnezia-wg-configs.zip'
            : 'amnezia-vpn-configs.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the browser has time to commit the download — Safari
      // races on immediate revoke and downloads zero-byte files.
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Не удалось собрать архив.');
    } finally {
      setBusyArchive(null);
    }
  };

  // A batch is always minted on one server, so the protocol is uniform; check
  // every entry anyway rather than trusting the first.
  const allVless = entries.every((e) => isVlessClient(e.client));
  const allHaveVpnUrl = entries.every((e) => !isVlessClient(e.client) && e.client.vpnUrl != null);

  return (
    <section className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
      <h2 className="text-sm font-semibold text-amber-200">
        Сохраните {entries.length}{' '}
        {pluralRu(entries.length, 'конфиг', 'конфига', 'конфигов')} прямо сейчас
      </h2>
      <p className="mt-1 text-xs text-slate-300">
        Приватные ключи показываются ровно один раз — после подтверждения они будут удалены. Если
        потеряете их, конфиги придётся перевыпустить.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {allVless ? (
          <button
            onClick={() => downloadArchive('vless')}
            disabled={busyArchive !== null}
            className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busyArchive === 'vless' ? 'Собираем…' : 'Скачать архив ссылок'}
          </button>
        ) : (
          <>
            <button
              onClick={() => downloadArchive('amnezia')}
              disabled={busyArchive !== null || !allHaveVpnUrl}
              title={
                allHaveVpnUrl
                  ? undefined
                  : 'Часть конфигов не содержит vpn://-ссылки (старые версии API). Перевыпустите их.'
              }
              className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyArchive === 'amnezia' ? 'Собираем…' : 'Скачать архив vpn://'}
            </button>
            <button
              onClick={() => downloadArchive('awg')}
              disabled={busyArchive !== null}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyArchive === 'awg' ? 'Собираем…' : 'Скачать архив .conf'}
            </button>
          </>
        )}
      </div>
      {archiveError && (
        <p className="mt-2 text-xs text-red-300">{archiveError}</p>
      )}

      <ul className="mt-5 space-y-3">
        {entries.map((entry) => (
          <li key={entry.config.id}>
            <details className="group rounded-lg border border-slate-800 bg-slate-950/40 p-4 open:border-amber-500/30">
              <summary className="flex cursor-pointer select-none items-center justify-between gap-3 text-sm">
                <span className="font-medium text-slate-100">{entry.config.name}</span>
                <span className="font-mono text-[11px] text-slate-500">
                  {entry.config.clientIp ?? 'VLESS'}
                </span>
              </summary>
              <div className="mt-4">
                <ClientConfigPanel
                  client={entry.client}
                  configName={entry.config.name}
                />
              </div>
            </details>
          </li>
        ))}
      </ul>

      <button
        onClick={onAcknowledge}
        className="mt-6 w-full rounded-lg border border-amber-500/40 bg-amber-500/10 py-2 text-sm font-medium text-amber-100 hover:bg-amber-500/20"
      >
        Я сохранил все конфиги — стереть приватные ключи
      </button>
    </section>
  );
}

/**
 * The bytes one config contributes to a bulk archive. A VLESS config has a
 * single representation, so the archive kind only decides the file extension;
 * an AWG config falls back to its `.conf` text when the `vpn://` wrapper is
 * missing (payloads minted before that format existed).
 */
function archiveBodyFor(
  client: SerializedClientConfig,
  kind: 'awg' | 'amnezia' | 'vless',
): string {
  if (isVlessClient(client)) return client.vlessUrl;
  if (kind === 'awg') return client.text;
  return client.vpnUrl ?? client.text;
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}
